import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('flexx_access_token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const DailyQuestPanel = ({ user }) => {
  const [quests, setQuests] = useState(null);
  const [timeLeft, setTimeLeft] = useState(86400);
  const [loading, setLoading] = useState(true);
  const [xpToast, setXpToast] = useState(null);

  useEffect(() => { fetchQuests(); }, []);

  useEffect(() => {
    if (!quests) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { clearInterval(timer); fetchQuests(); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quests?.date]);

  const fetchQuests = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/flexx/quests`, {
        headers: authHeaders(),
        withCredentials: true,
      });
      setQuests(res.data);
      setTimeLeft(Math.floor(res.data.time_remaining || 86400));
    } catch (err) {
      console.error('[DailyQuestPanel] fetchQuests failed', err);
    } finally {
      setLoading(false);
    }
  };

  const completeQuest = async (questId) => {
    try {
      const res = await axios.post(
        `${API}/flexx/quests/${questId}/progress`,
        null,
        { params: { increment: 1 }, headers: authHeaders(), withCredentials: true }
      );
      if (res.data.xp_earned > 0) {
        setXpToast({ amount: res.data.xp_earned, isBonus: res.data.bonus_xp > 0 });
        setTimeout(() => setXpToast(null), 3000);
        window.dispatchEvent(new CustomEvent('flexx:xp-updated', { detail: { amount: res.data.xp_earned } }));
      }
      fetchQuests();
    } catch (err) {
      console.error('[DailyQuestPanel] completeQuest failed', err);
    }
  };

  const formatTime = (s) => {
    const t = Math.max(0, Math.floor(s));
    const h = Math.floor(t / 3600);
    const m = Math.floor((t % 3600) / 60);
    const sec = t % 60;
    return `${h}h ${m}m ${sec}s`;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading quests...</div>;
  if (!quests) return null;

  const completedCount = quests.quests.filter((q) => q.completed).length;
  const totalCount = quests.quests.length;
  const progressPercent = totalCount ? (completedCount / totalCount) * 100 : 0;
  const allCompleted = quests.all_completed;

  const panelStyle = { background: '#0C0C0C', border: '1px solid #222', borderRadius: '16px', padding: '24px', marginBottom: '24px', color: '#fff', position: 'relative' };
  const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', gap: 12, flexWrap: 'wrap' };
  const titleStyle = { margin: 0, fontSize: '18px', fontWeight: 700, color: '#C9A227', letterSpacing: '2px', textTransform: 'uppercase' };
  const timerStyle = { background: '#111', padding: '8px 16px', borderRadius: '20px', fontFamily: 'monospace', fontSize: '14px', color: '#FF4444', fontWeight: 700 };
  const progressBg = { width: '100%', height: '8px', background: '#222', borderRadius: '4px', marginBottom: '24px', overflow: 'hidden' };
  const progressFill = { height: '100%', borderRadius: '4px', transition: 'width 0.8s ease', width: `${progressPercent}%`, background: allCompleted ? 'linear-gradient(90deg, #22C55E, #4ade80)' : 'linear-gradient(90deg, #C9A227, #e8d5a3)' };

  return (
    <div style={panelStyle} data-testid="daily-quest-panel">
      {xpToast && (
        <div
          data-testid="quest-xp-toast"
          style={{ position: 'absolute', top: 16, right: 16, zIndex: 100, background: 'linear-gradient(135deg, #C9A227, #e8d5a3)', color: '#000', padding: '16px 20px', borderRadius: '12px', boxShadow: '0 8px 32px rgba(201,162,39,0.4)' }}
        >
          <div style={{ fontSize: 16, fontWeight: 700 }}>+{xpToast.amount} XP!</div>
          {xpToast.isBonus && <div style={{ fontSize: 11, fontWeight: 600 }}>MYSTERY BONUS UNLOCKED</div>}
        </div>
      )}

      <div style={headerStyle}>
        <div>
          <h2 style={titleStyle}>📋 TODAY'S QUESTS</h2>
          <span style={{ fontSize: '13px', color: '#888', marginTop: '4px', display: 'block' }}>
            {completedCount}/{totalCount} completed
          </span>
        </div>
        <div data-testid="quest-timer" style={timerStyle}>⏱️ {formatTime(timeLeft)}</div>
      </div>

      <div style={progressBg}>
        <div data-testid="quest-progress-bar" style={progressFill} aria-valuenow={Math.round(progressPercent)} aria-valuemin={0} aria-valuemax={100} role="progressbar" />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {quests.quests.map((quest) => (
          <div
            key={quest.id}
            data-testid={`quest-card-${quest.id}`}
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: '#111', borderRadius: '12px', border: `1px solid ${quest.completed ? '#22C55E' : '#333'}`, opacity: quest.completed ? 0.6 : 1, gap: 12, flexWrap: 'wrap' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: quest.completed ? '#22C55E' : '#C9A227', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                {quest.completed ? '✓' : quest.icon}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '15px', fontWeight: 600, color: quest.completed ? '#22C55E' : '#fff' }}>{quest.title}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{quest.description}</div>
                <div style={{ fontSize: '11px', color: '#C9A227', marginTop: '4px', fontFamily: 'monospace' }}>
                  {quest.current_count}/{quest.target_count}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
              <span style={{ fontSize: '13px', color: '#C9A227', fontWeight: 700, fontFamily: 'monospace' }}>+{quest.xp_reward} XP</span>
              {!quest.completed && (
                <button
                  data-testid={`quest-complete-btn-${quest.id}`}
                  onClick={() => completeQuest(quest.id)}
                  style={{ padding: '8px 16px', background: '#C9A227', color: '#000', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Complete
                </button>
              )}
              {quest.completed && <span style={{ fontSize: '12px', color: '#22C55E', fontWeight: 600 }}>Done ✓</span>}
            </div>
          </div>
        ))}
      </div>

      <div
        data-testid="quest-bonus-section"
        style={{ marginTop: '20px', padding: '20px', borderRadius: '12px', border: `2px ${allCompleted ? 'solid' : 'dashed'} ${allCompleted ? '#C9A227' : '#333'}`, background: allCompleted ? 'linear-gradient(135deg, rgba(201,162,39,0.15), rgba(201,162,39,0.05))' : 'linear-gradient(135deg, #111, #0a0a0a)', textAlign: 'center' }}
      >
        <div style={{ fontSize: '16px', fontWeight: 700, color: allCompleted ? '#C9A227' : '#666', marginBottom: '8px' }}>
          {allCompleted ? '🎉 MYSTERY BONUS UNLOCKED!' : '🎁 MYSTERY BONUS'}
        </div>
        <p style={{ fontSize: '13px', color: '#888', margin: 0 }}>
          {allCompleted ? `+${quests.bonus_xp} XP + Rare Badge Fragment awarded!` : 'Complete ALL 5 quests to unlock +200 XP bonus'}
        </p>
        {allCompleted && !quests.bonus_claimed && (
          <button
            data-testid="quest-claim-bonus-btn"
            onClick={fetchQuests}
            style={{ marginTop: '12px', width: '100%', padding: '12px', background: 'linear-gradient(135deg, #C9A227, #e8d5a3)', color: '#000', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}
          >
            Claim Reward!
          </button>
        )}
      </div>
    </div>
  );
};

export default DailyQuestPanel;
